import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { WorkflowTypes, workflowModalState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import useDeidentificationChoice from '../useDeidentificationChoice';
import { useRegistrationBackend, useSessionID } from '../useBackend';

export type BackendContourPropagationTaskInput = {
    selectedModel: PredictionModel;
    targetImageSeriesUid?: string;
    selectedStructures?: string[];
    customizationName?: string;
};

/**
 * A React hook for registering and propagating structures between images based on the selected model.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useRegistration() {
    const primaryImage = useSelectedPrimaryImage();

    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const chooseDeidentification = useDeidentificationChoice();
    const session = useSessionOrNull();
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const patient = useSelectedPatient();
    const registrationClient = useRegistrationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizer, anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    const resultStructureSet = processedItems.structureSets[0];
                    const targetRegistration = processedItems.registrations[0];
                    const targetImage = resultStructureSet.image;
                    if (
                        resultStructureSet &&
                        targetImage &&
                        targetRegistration
                    ) {
                        initializeEnvironment({
                            structureSets: [resultStructureSet],
                            images: [targetImage],
                            registrations: [targetRegistration],
                            doses: [],
                            patients: [],
                            interpolatedImages: [],
                            skippedSeries: [],
                        });
                    }
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (
        input: BackendContourPropagationTaskInput
    ) => {
        if (isSubmitting) {
            return;
        }

        const blobsToUpload: Blob[] = [];
        // Resolved at Start, so an image loaded since the last render is found.
        const registeredImage = patient?.images.find(
            (image) =>
                image !== primaryImage &&
                image.seriesUid === input.targetImageSeriesUid
        );
        if (
            !primaryImage ||
            !registeredImage ||
            !input.selectedModel ||
            !currentStructureSet
        ) {
            const message = 'Missing required data for registration';
            handleError(new Error(message), message);
            return;
        }

        setIsSubmitting(true);
        try {
            if (
                !ensureStructureSetsCurrent(
                    getDatasetEnvironmentRegistry(),
                    [currentStructureSet],
                    'Registration'
                )
            ) {
                return;
            }
            if (!session) {
                throw new Error('No active patient session for registration.');
            }
            const choice = await chooseDeidentification();
            if (!choice) {
                return;
            }
            const pseudonymize = choice.deidentify;
            resetSessionID();
            const taskUID = `${sessionID}.${v4().split('-')[0]}`;

            const primaryImageBlobs = Array.from(
                primaryImage.getSlicesAsDicomBlobs()
            );
            const targetImageBlobs = Array.from(
                registeredImage.getSlicesAsDicomBlobs()
            );
            const totalSlices =
                primaryImageBlobs.length + targetImageBlobs.length + 1; // adds structure set slice

            const modelIdentifier = input.customizationName
                ? `${input.selectedModel.name}.${input.customizationName}`
                : input.selectedModel.name;

            const backendParameters: RequestParameters = {
                action: modelIdentifier,
                movingSeriesUID: primaryImage?.seriesUid || '',
                fixedSeriesUID: input.targetImageSeriesUid || '',
                nExpectedFiles: totalSlices.toString(),
                taskUID,
                sessionId: sessionID,
            };

            const primaryBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, primaryImageBlobs)
                : primaryImageBlobs;
            const targetBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, targetImageBlobs)
                : targetImageBlobs;

            // The map holds every earlier de-identified send, so it applies
            // only when these files are de-identified too.
            if (pseudonymize && anonymizer.UIDMapping) {
                if (
                    backendParameters.movingSeriesUID &&
                    backendParameters.movingSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.movingSeriesUID =
                        anonymizer.UIDMapping[
                            backendParameters.movingSeriesUID
                        ];
                }

                if (
                    backendParameters.fixedSeriesUID &&
                    backendParameters.fixedSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.fixedSeriesUID =
                        anonymizer.UIDMapping[backendParameters.fixedSeriesUID];
                }
            }

            if (primaryBlobsToUpload) {
                blobsToUpload.push(...primaryBlobsToUpload);
            } else {
                handleError('Anonymization failed for primary image files.');
                return;
            }

            if (targetBlobsToUpload) {
                blobsToUpload.push(...targetBlobsToUpload);
            } else {
                handleError('Anonymization failed for target image files.');
                return;
            }

            // Derive the ROI subset from the read model the hook
            // already holds; the export service resolves the live SS.
            const selectedRoiIds = currentStructureSet.roiArray
                .filter((roi) => input.selectedStructures?.includes(roi.name))
                .map((roi) => roi.roiId);
            const structureSetBlob =
                session.structureSetExportService.exportBlob(
                    currentStructureSet.dicomUid,
                    { selectedRoiIds }
                );
            const structureSetBlobToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, [structureSetBlob])
                : [structureSetBlob];
            if (structureSetBlobToUpload) {
                blobsToUpload.push(...structureSetBlobToUpload);
            } else {
                handleError('Anonymization failed for structure set.');
                return;
            }

            const newJobDetails: NewJobData = {
                id: sessionID,
                name: 'Adapt+',
                totalItems: blobsToUpload.length,
                type: WorkflowTypes.ADAPT,
                client: registrationClient,
                onLoad: handleProcessedFiles,
                payload: {
                    filesToUpload: blobsToUpload.map((file) => ({ file })),
                    backendParameters,
                    pseudonymized: pseudonymize,
                },
                task: {
                    model: input.selectedModel,
                },
                inputApprovalStatus: currentStructureSet.approvalStatus,
            };

            addJob(newJobDetails);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start registration task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startProcessing, isSubmitting };
}
